/**
 * Long / Short Position (risk-reward) — canvas renderer.
 *
 * briefs/LONG_SHORT_POSITION_TOOL_BRIEF.md §3.2. Written ONCE as a function of
 * (ctx, drawing, coordToPixel) so the single-layout overlay (which projects with
 * the page's chartCoordToPixel) and the multi-pane overlay (which projects with
 * each pane's own closure) call the same code. The other drawing types have
 * per-layout copies, which is how redrawOverlayForPane came to draw only 7 of 12
 * types; this tool must not add to that.
 *
 * Draws nothing for an invalid position (computePositionMetrics -> null): the
 * renderer never prints a guess. Selection highlight and handles are drawn by the
 * page's existing drawHighlight/drawHandles, which read p1/p2/p3.
 *
 * No module-level state; no work at import time (this module loads on every page).
 */

import { hexToRgba } from "../domain/format";
import { getLineDashPattern } from "./line_styles";
import { POSITION_COLORS, computePositionMetrics, formatPositionLabels } from "./position_tool";

export type CoordToPixel = (time: number, price: number) => { x: number; y: number } | null;

/** The subset of CanvasRenderingContext2D this renderer uses (keeps it DOM-free for tests). */
export interface PositionRenderContext {
  save(): void;
  restore(): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number): void;
  measureText(text: string): { width: number };
  setLineDash(segments: number[]): void;
  fillStyle: any;
  strokeStyle: any;
  lineWidth: number;
  font: string;
  textBaseline: any;
  textAlign: any;
}

/** Alpha of the profit / loss box fills at opacity 1 (the parallel channel uses 0.08). */
export const FILL_ALPHA = 0.16;
const LABEL_FONT = "11px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const PILL_PAD_X = 6;
const PILL_HEIGHT = 16;
const PILL_GAP = 3;
const PILL_TEXT = "#FFFFFF";

function line(ctx: PositionRenderContext, x1: number, y1: number, x2: number, y2: number, color: string, width: number, dash: number[]): void {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dash);
  ctx.stroke();
}

/** A filled label pill; `top` is its top edge. X is clamped into [visL, visR]. */
function pill(
  ctx: PositionRenderContext,
  text: string,
  centerX: number,
  top: number,
  visL: number,
  visR: number,
  bg: string,
  opacity: number,
): void {
  const w = ctx.measureText(text).width + PILL_PAD_X * 2;
  const x = Math.max(visL + 2, Math.min(centerX - w / 2, visR - w - 2));
  ctx.fillStyle = hexToRgba(bg, 0.9 * opacity);
  ctx.fillRect(x, top, w, PILL_HEIGHT);
  ctx.fillStyle = hexToRgba(PILL_TEXT, opacity);
  ctx.fillText(text, x + PILL_PAD_X, top + PILL_HEIGHT / 2);
}

/**
 * Top edge for a stop/target pill: inside the box just under its far line when the
 * box is tall enough, otherwise just outside that line. `inwardDown` is true when
 * the entry lies below the line (screen y grows downward).
 */
function farLinePillTop(lineY: number, entryY: number): number {
  const inwardDown = entryY > lineY;
  const boxHeight = Math.abs(entryY - lineY);
  const fitsInside = boxHeight >= PILL_HEIGHT + PILL_GAP * 2;
  if (fitsInside) return inwardDown ? lineY + PILL_GAP : lineY - PILL_GAP - PILL_HEIGHT;
  return inwardDown ? lineY - PILL_GAP - PILL_HEIGHT : lineY + PILL_GAP;
}

/**
 * Draws one Long/Short Position. Returns true if anything was drawn, false for an
 * invalid drawing or one whose points do not project (off-scale).
 */
export function drawPosition(
  ctx: PositionRenderContext,
  d: any,
  coordToPixel: CoordToPixel,
  canvasWidth: number,
): boolean {
  const metrics = computePositionMetrics(d);
  const labels = formatPositionLabels(d);
  if (!metrics || !labels) return false;

  const p1 = coordToPixel(d.p1.time, d.p1.price);
  const p2 = coordToPixel(d.p2.time, d.p2.price);
  const p3 = coordToPixel(d.p3.time, d.p3.price);
  if (!p1 || !p2 || !p3) return false;

  const left = p1.x;
  const right = p2.x; // p2.time === p3.time, so p3.x is the same
  if (!(right > left)) return false;
  const width = right - left;
  const yEntry = p1.y;
  const yStop = p2.y;
  const yTarget = p3.y;

  const opacity = d.opacity ?? 1;
  const lineWidth = d.lineWidth || 1;
  const dash = getLineDashPattern(d.lineStyle ?? 0, "canvas");
  const entryColor = d.color || POSITION_COLORS.entry;
  const profitColor = d.profitColor || POSITION_COLORS.profit;
  const lossColor = d.lossColor || POSITION_COLORS.loss;

  ctx.save();

  // Boxes first, so lines and labels render on top.
  ctx.fillStyle = hexToRgba(profitColor, FILL_ALPHA * opacity);
  ctx.fillRect(left, Math.min(yEntry, yTarget), width, Math.abs(yTarget - yEntry));
  ctx.fillStyle = hexToRgba(lossColor, FILL_ALPHA * opacity);
  ctx.fillRect(left, Math.min(yEntry, yStop), width, Math.abs(yStop - yEntry));

  line(ctx, left, yTarget, right, yTarget, hexToRgba(profitColor, opacity), lineWidth, dash);
  line(ctx, left, yStop, right, yStop, hexToRgba(lossColor, opacity), lineWidth, dash);
  line(ctx, left, yEntry, right, yEntry, hexToRgba(entryColor, opacity), lineWidth, dash);
  ctx.setLineDash([]);

  // Labels, centred in the part of the box that is actually on screen.
  const visL = Math.max(left, 0);
  const visR = Math.min(right, canvasWidth);
  const centerX = (visL + visR) / 2;
  ctx.font = LABEL_FONT;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";

  pill(ctx, labels.target, centerX, farLinePillTop(yTarget, yEntry), visL, visR, profitColor, opacity);
  pill(ctx, labels.stop, centerX, farLinePillTop(yStop, yEntry), visL, visR, lossColor, opacity);
  pill(ctx, `${labels.side}  ${labels.rr}`, centerX, yEntry - PILL_HEIGHT / 2, visL, visR, entryColor, opacity);

  if (d.title) {
    ctx.fillStyle = hexToRgba(entryColor, opacity);
    ctx.textBaseline = "bottom";
    ctx.fillText(String(d.title), visL + 2, Math.min(yEntry, yStop, yTarget) - PILL_GAP);
  }

  ctx.restore();
  return true;
}
